import {
  standardRequestWithFlash,
  standardRequestWithErrorFlash,
} from '../../../shared/utils/request';
import type {
  Address,
  CandidateDetails,
  Education,
  Employment,
  CandidateName,
  CurrentJob,
  Email,
  PhoneNumber,
  Website,
} from '../../shared/types';
import { LocationPayload } from '../../shared/types';
import type { Dispatch, State } from '../use_candidate_profile';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { CustomField } from '../../shared/custom_fields/types';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('candidate_profile_redesign.candidate_details');

type AddCandidateDetails = {
  type: 'addCandidateDetails';
  payload: CandidateDetails;
};

export const getCandidateDetails =
  (state: State, dispatch: Dispatch) => async () => {
    dispatch({
      type: 'setStatus',
      payload: { key: 'candidateDetails', type: 'loading' },
    });
    const {
      paths: { getCandidateDetailsPath },
    } = state;
    try {
      const {
        data: { candidateName, ...otherData },
      } = await standardRequestWithErrorFlash.get(getCandidateDetailsPath);
      dispatch({ type: 'addCandidateDetails', payload: otherData });
      dispatch({
        type: 'setStatus',
        payload: { key: 'candidateDetails', type: 'complete' },
      });
    } catch (e: any) {
      dispatch({
        type: 'setStatus',
        payload: { key: 'candidateDetails', type: 'error', context: e },
      });
    }
  };

type EditCandidateDetails = {
  type: 'editDetails';
  payload: CandidateDetails;
};

type EditCandidateName = {
  type: 'editCandidateName';
  payload: CandidateName;
};

type EditTags = {
  type: 'editTags';
  payload: Array<Item>;
};

type SetDoNotEmail = {
  type: 'setDoNotEmail';
  payload: boolean;
};

export const setDoNotEmail =
  (state: State, dispatch: Dispatch) => async (doNotEmail: boolean) => {
    dispatch({ type: 'setDoNotEmail', payload: doNotEmail });
  };

type EditCandidateDetailsPayload =
  | {
      candidate_name?: Pick<
        CandidateName,
        'first_name' | 'last_name' | 'preferred_name'
      >;
      education?: Array<Education>;
      employment?: Array<Employment>;
      address?: Array<Address>;
      current_job?: CurrentJob;
      custom_field?: CustomField;
      timezone?: string;
      email?: Array<Email>;
      phone?: Array<PhoneNumber>;
      website?: Array<Website>;
      location?: LocationPayload;
    }
  | {
      tags: Array<Item>;
    };
export const editCandidateDetails =
  (state: State, dispatch: Dispatch) =>
  async (formData: EditCandidateDetailsPayload) => {
    const {
      paths: { editCandidateDetailsPath },
    } = state;
    try {
      const result = await standardRequestWithFlash.put(
        editCandidateDetailsPath,
        formData
      );
      if ('candidate_name' in formData) {
        dispatch({
          type: 'editCandidateName',
          payload: {
            ...result.data.candidateName,
          },
        });
      } else if ('tags' in formData) {
        dispatch({
          type: 'editTags',
          payload: [...formData.tags!],
        });
      } else {
        dispatch({ type: 'editDetails', payload: result.data });
      }
    } catch (e: any) {
      console.log('Error editing candidate details', e);
    }
  };

export const deleteEmployment =
  (state: State, dispatch: Dispatch) => (employmentId: string) => {
    const {
      paths: { deleteEmploymentPath },
      candidateDetails,
    } = state;

    // This makes flow happy
    if (!candidateDetails) {
      return;
    }

    try {
      standardRequestWithErrorFlash.delete(deleteEmploymentPath, {
        data: { employmentId },
        successMessage: t('employment.delete_success'),
      });
      const employments = candidateDetails.details.employments.filter(
        (e) => e.id !== employmentId
      );
      dispatch({
        type: 'editDetails',
        payload: {
          ...candidateDetails,
          details: { ...candidateDetails.details, employments },
        },
      });
    } catch (e: any) {
      console.log('Error deleting employment', e);
    }
  };

export type Actions =
  | AddCandidateDetails
  | EditCandidateDetails
  | EditCandidateName
  | EditTags
  | SetDoNotEmail;
